import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/atoms/Button";
import MainLayoutDetail from "../../components/layout/MainLayoutDetail";
import SectionBox from "../../components/layout/SectionBox";
import { toast } from "react-toastify";

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token") || "";
  const emailParam = queryParams.get("email") || "";

  const [formData, setFormData] = useState({
    email: emailParam,
    password: "",
    password_confirmation: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (formData.password !== formData.password_confirmation) {
      toast.error("Password and confirmation do not match");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        "https://admin.talentlinkindo.com/api/reset-password",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            password_confirmation: formData.password_confirmation,
            token,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data?.errors?.[0] || data?.message || "Something went wrong";
        toast.error(errorMessage);
        setError(errorMessage);
      } else {
        toast.success(data.message || "Password reset successful");
        navigate("/en/login");
      }
    } catch (err: any) {
      const message = err?.message || "Something went wrong";
      toast.error(message);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayoutDetail>
      <div className="container-section mx-auto mt-32 px-6">
        <SectionBox
          className="md:flex md:flex-col md:justify-center md:min-h-[70vh]"
          titleClassName="text-2xl md:text-3xl text-white"
          background="radial-gradient(circle at 50% -100%, rgba(255,255,255,0) 0%, #123b7c 70%)"
        >
          <div className="flex items-center justify-center">
            <form
              onSubmit={handleSubmit}
              className="bg-white p-4 md:p-8 rounded-2xl shadow-md w-96"
            >
              <h2 className="text-2xl font-semibold mb-2 text-gray-800 text-center">
                Reset Password
              </h2>

              <p className="text-sm mb-6 text-center">
                Enter your new password to reset your account password
              </p>

              <div className="flex flex-col gap-4">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  disabled
                  className="border border-gray-300 rounded-lg p-3 text-sm bg-gray-100 cursor-not-allowed"
                />

                <input
                  type="password"
                  name="password"
                  placeholder="New Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  className="border border-gray-300 rounded-lg p-3 text-sm"
                />

                <input
                  type="password"
                  name="password_confirmation"
                  placeholder="Confirm Password"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  required
                  minLength={8}
                  className="border border-gray-300 rounded-lg p-3 text-sm"
                />

                {error && (
                  <p className="text-sm text-red-500 text-center">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full bg-primary hover:bg-secondary ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </div>
            </form>
          </div>
        </SectionBox>
      </div>
    </MainLayoutDetail>
  );
}
